/* ==========================================================================
   wk-fiche-technique.js — v6
   Génère la fiche technique PDF Welkit dans le navigateur, sans app ni serveur.
   pdfmake est chargé en lazy au premier clic.

   En-tête logo + filet, mention de propriété verticale en marge gauche,
   sections en filets fins, galerie 1 grande + 4 vignettes, échelle Pro-Score
   complète, mentions légales en pied. Pas de filigrane.

   Note : pdfmake ne sait pas pivoter du texte. La mention verticale de la
   marge est donc rendue dans un canvas puis insérée comme image.

   Dépendances : snippets/wk-fiche-technique.liquid
                 assets/wk-logo.png, assets/wk-filigrane.png
   ========================================================================== */

(function () {
  'use strict';

  var PDFMAKE_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.10/pdfmake.min.js';
  var VFS_SRC     = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.10/vfs_fonts.js';

  var KAKI    = '#090302';   /* bandeaux de rubrique : noir de la charte */
  var BLEU    = '#30718E';
  var ORANGE  = '#FFA401';   /* ambre unique de la charte */
  var NOIR    = '#090302';
  var GRIS    = '#F3F3F3';   /* fond de section clair de la charte */
  var GRIS_L  = '#E1E1E1';   /* filets et separateurs */
  var GRIS_TX = '#6B7178';   /* gris de texte neutre */

  var PT = 2.8346;              // 1 mm en points
  var PAGE_W = 595.28, PAGE_H = 841.89;
  var MG = 17 * PT;             // marge gauche
  var MD = 15 * PT;             // marge droite
  var MH = 24 * PT;             // marge haute
  var MB = 17 * PT;             // marge basse
  var LARG = PAGE_W - MG - MD;

  var libPromise = null;

  /* ------------------------------------------------------------------ outils */

  function injecter(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('Chargement impossible : ' + src)); };
      document.head.appendChild(s);
    });
  }

  function chargerLib() {
    if (libPromise) return libPromise;
    libPromise = (function () {
      if (window.pdfMake && window.pdfMake.vfs) return Promise.resolve(window.pdfMake);
      return injecter(PDFMAKE_SRC)
        .then(function () {
          if (!window.pdfMake) throw new Error('pdfMake absent après chargement');
          return injecter(VFS_SRC);
        })
        .then(function () {
          if (!window.pdfMake.vfs) throw new Error('vfs_fonts non appliqué');
          return window.pdfMake;
        });
    })();
    return libPromise;
  }

  function paires(raw) {
    if (!raw) return [];
    return raw.split('|;;|').filter(Boolean).map(function (b) { return b.split('|~|'); });
  }

  function liste(raw) {
    if (!raw) return [];
    return raw.split('|;;|').filter(Boolean);
  }

  function dateFR() {
    var d = new Date();
    var z = function (n) { return n < 10 ? '0' + n : '' + n; };
    return z(d.getDate()) + '/' + z(d.getMonth() + 1) + '/' + d.getFullYear();
  }

  function lettreProScore(t) {
    if (!t) return null;
    var m = String(t).match(/PRO-?SCORE\s*([A-E])/i);
    return m ? m[1].toUpperCase() : null;
  }

  function libelleProScore(t) {
    if (!t) return '';
    var p = String(t).split(':');
    return (p.length > 1 ? p.slice(1).join(':') : p[0]).trim();
  }

  /* Nom de fichier court et lisible : "FT - Chaussures Mil-Tec Squad 5.pdf".
     On coupe la parenthese, on nettoie les caracteres interdits par les systemes
     de fichiers, on tronque sur une frontiere de mot et on retire les mots de
     liaison qui traineraient en fin de nom. */
  var LIAISONS = ['pour', 'avec', 'sans', 'dans', 'sur', 'de', 'du', 'des', 'et',
                  'a', 'à', 'en', 'par', 'le', 'la', 'les', 'un', 'une', 'au', 'aux'];
  var NOM_MAX = 58;

  function titreCourt(titre) {
    var t = String(titre || 'Produit');
    t = t.split('(')[0];
    t = t.replace(/[\/\\:*?"<>|]+/g, '-');
    t = t.replace(/\s+/g, ' ').trim();
    if (t.length > NOM_MAX) {
      t = t.slice(0, NOM_MAX);
      var i = t.lastIndexOf(' ');
      if (i > 24) t = t.slice(0, i);
    }
    var mots = t.split(' ');
    while (mots.length > 3 &&
           LIAISONS.indexOf(mots[mots.length - 1].toLowerCase()) !== -1) {
      mots.pop();
    }
    t = mots.join(' ').replace(/[\s,;:.\-–]+$/, '');
    return t || 'Produit';
  }

  function nomFichier(d) {
    return 'FT - ' + titreCourt(d.titre) + '.pdf';
  }

  function imageDataURL(url) {
    if (!url) return Promise.resolve(null);
    return fetch(url, { mode: 'cors', credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('img'); return r.blob(); })
      .then(function (b) {
        return new Promise(function (res, rej) {
          var fr = new FileReader();
          fr.onload = function () { res(fr.result); };
          fr.onerror = rej;
          fr.readAsDataURL(b);
        });
      })
      .catch(function () { return null; });
  }

  function chargerImages(urls) {
    return Promise.all(urls.map(imageDataURL)).then(function (r) {
      return r.filter(Boolean);
    });
  }

  /* Texte pivoté à 90° : pdfmake ne le fait pas, on passe par un canvas. */
  function texteVertical(txt, hauteurPt) {
    try {
      var ech = 3;                                  // suréchantillonnage
      var taille = 6.4;
      var c = document.createElement('canvas');
      c.width = Math.round(12 * ech);
      c.height = Math.round(hauteurPt * ech);
      var g = c.getContext('2d');
      g.scale(ech, ech);
      g.translate(0, hauteurPt);
      g.rotate(-Math.PI / 2);
      g.fillStyle = GRIS_TX;
      g.font = taille + 'px Helvetica, Arial, sans-serif';
      g.textBaseline = 'top';
      g.fillText(txt, 0, 1);
      return c.toDataURL('image/png');
    } catch (e) { return null; }
  }

  /* ---------------------------------------------------------- briques de mise en page */

  function titreSection(txt) {
    return {
      table: { widths: ['*'], body: [[{ text: String(txt).toUpperCase(), style: 'sec' }]] },
      layout: {
        hLineWidth: function () { return 0; },
        vLineWidth: function () { return 0; },
        fillColor: function () { return KAKI; },
        paddingLeft: function () { return 7; },
        paddingRight: function () { return 7; },
        paddingTop: function () { return 5; },
        paddingBottom: function () { return 5; }
      },
      margin: [0, 0, 0, 10]
    };
  }

  /* Un titre ne doit jamais rester seul en bas de page. pdfmake ne sait pas
     "garder avec le suivant", donc on scinde en deux noeuds : le titre et les
     GARDE premieres lignes forment un bloc insecable, le reste se coupe
     librement. Les deux tableaux ont les memes largeurs et le zebrage se
     poursuit d'un bloc a l'autre : la jointure est invisible. */
  var GARDE = 2;

  function sectionCleValeur(titre, rows, largCle, styleCle) {
    var ligne = function (r) {
      return [
        { text: r[0] || '', style: styleCle, margin: [7, 5, 8, 5] },
        { text: r[1] || '', style: 'cv', margin: [0, 5, 7, 5] }
      ];
    };
    var vide = function () {
      return {
        hLineWidth: function () { return 0; },
        vLineWidth: function () { return 0; },
        paddingLeft: function () { return 0; },
        paddingRight: function () { return 0; },
        paddingTop: function () { return 0; },
        paddingBottom: function () { return 0; }
      };
    };

    var tete = rows.slice(0, GARDE);
    var reste = rows.slice(GARDE);
    var noeuds = [];

    var body1 = [[
      { text: String(titre).toUpperCase(), style: 'sec', colSpan: 2,
        margin: [7, 5, 7, 5] },
      {}
    ]];
    tete.forEach(function (r) { body1.push(ligne(r)); });

    var l1 = vide();
    l1.fillColor = function (i) {
      if (i === 0) return KAKI;
      return ((i - 1) % 2 === 0) ? GRIS : null;
    };
    noeuds.push({
      table: { widths: [largCle, '*'], body: body1 },
      layout: l1,
      unbreakable: true
    });

    if (reste.length) {
      var l2 = vide();
      l2.fillColor = function (i) {
        return ((i + GARDE) % 2 === 0) ? GRIS : null;
      };
      noeuds.push({
        table: { widths: [largCle, '*'], body: reste.map(ligne) },
        layout: l2
      });
    }

    return { stack: noeuds, margin: [0, 0, 0, 18] };
  }

  /* Meme principe pour les tableaux a colonnes : titre, en-tetes et GARDE
     premieres lignes soudes, le reste coupable. Rien ne se repete. */
  function sectionTableau(titre, entetes, lignes, largeurs) {
    var cellules = function (r, style) {
      return r.map(function (cel) {
        return { text: cel || '—', style: style, margin: [0, 4.5, 7, 4.5] };
      });
    };
    var vide = function () {
      return {
        hLineWidth: function () { return 0; },
        vLineWidth: function () { return 0; },
        paddingLeft: function (i) { return i === 0 ? 7 : 0; },
        paddingRight: function () { return 0; },
        paddingTop: function () { return 0; },
        paddingBottom: function () { return 0; }
      };
    };

    var tete = lignes.slice(0, GARDE);
    var reste = lignes.slice(GARDE);
    var noeuds = [];

    var titreCell = { text: String(titre).toUpperCase(), style: 'sec',
                      colSpan: entetes.length, margin: [7, 5, 7, 5] };
    var body1 = [[titreCell]];
    for (var k = 1; k < entetes.length; k++) body1[0].push({});
    body1.push(cellules(entetes, 'th'));
    tete.forEach(function (r) { body1.push(cellules(r, 'td')); });

    var l1 = vide();
    l1.fillColor = function (i) {
      if (i === 0) return KAKI;
      if (i === 1) return GRIS_L;
      return ((i - 2) % 2 === 1) ? GRIS : null;
    };
    noeuds.push({ table: { widths: largeurs, body: body1 }, layout: l1,
                  unbreakable: true });

    if (reste.length) {
      var l2 = vide();
      l2.fillColor = function (i) {
        return ((i + GARDE) % 2 === 1) ? GRIS : null;
      };
      noeuds.push({
        table: { widths: largeurs, body: reste.map(function (r) { return cellules(r, 'td'); }) },
        layout: l2
      });
    }

    return { stack: noeuds, margin: [0, 0, 0, 18] };
  }

  /* Cadre strictement carre autour d'une image, comme la galerie du site. */
  function cadreImage(data, cote) {
    var dedans = cote - 8;
    return {
      width: cote,
      table: {
        widths: [dedans], heights: [dedans],
        body: [[data
          ? { image: data, fit: [dedans, dedans], alignment: 'center' }
          : { text: '' }]]
      },
      layout: {
        hLineWidth: function () { return 0.5; },
        vLineWidth: function () { return 0.5; },
        hLineColor: function () { return GRIS_L; },
        vLineColor: function () { return GRIS_L; },
        paddingLeft: function () { return 4; },
        paddingRight: function () { return 4; },
        paddingTop: function () { return 4; },
        paddingBottom: function () { return 4; }
      }
    };
  }

  /* ------------------------------------------------------------ document */

  function construire(d, res) {
    var edition = dateFR();
    var carac  = paires(d.caracRaw);
    var phares = paires(d.pharesRaw);
    var ps     = paires(d.psRaw);
    var vars   = paires(d.varRaw);
    var lettre = lettreProScore(d.psNote);
    var c = [];

    /* titre + sous-titre + lien */
    c.push({ text: d.titre || '', style: 'h1' });

    var sous = [];
    if (d.marque)  sous.push({ text: 'Marque : ' },   { text: d.marque, bold: true, color: NOIR });
    if (d.ref)     sous.push({ text: '   ·   Réf. Welkit : ' }, { text: d.ref, bold: true, color: NOIR });
    c.push({ text: sous, style: 'sub' });

    if (d.lien) {
      c.push({
        text: [{ text: 'Lien : ', color: GRIS_TX },
               { text: d.lien, color: BLEU, link: 'https://' + d.lien }],
        fontSize: 9, margin: [0, 3, 0, 16]
      });
    }

    /* galerie : 1 grande + 4 vignettes, toutes carrees, gouttiere uniforme de 4 mm.
       Largeur : 89 + 2 + 43,5 + 2 + 43,5 = 178 mm, soit exactement la largeur utile.
       Hauteur de la grille : 43,5 + 2 + 43,5 = 89 mm, egale a celle de la grande. */
    var imgs = res.images || [];
    var GAP = 2 * PT;
    var COTE_G = 89 * PT;
    var COTE_V = 43.5 * PT;

    var vign = function (i, dernier) {
      if (!imgs[i]) return { text: '', margin: [0, 0, 0, dernier ? 0 : GAP] };
      var box = cadreImage(imgs[i], COTE_V);
      box.margin = [0, 0, 0, dernier ? 0 : GAP];
      return box;
    };

    c.push({
      columns: [
        cadreImage(imgs[0] || null, COTE_G),
        {
          width: '*',
          columns: [
            { width: COTE_V, stack: [vign(1, false), vign(3, true)] },
            { width: COTE_V, stack: [vign(2, false), vign(4, true)] }
          ],
          columnGap: GAP,
          margin: [GAP, 0, 0, 0]
        }
      ],
      columnGap: 0,
      margin: [0, 0, 0, 18]
    });

    /* présentation */
    if (d.intro) {
      c.push({
        unbreakable: true,
        stack: [titreSection('Présentation'),
                { text: d.intro, style: 'body' }],
        margin: [0, 0, 0, 16]
      });
    }

    /* points clés — avant les caractéristiques */
    if (phares.length) {
      c.push(sectionCleValeur('Points clés', phares, 54 * PT, 'pht'));
    }

    /* caractéristiques techniques */
    if (carac.length) {
      c.push(sectionCleValeur('Caractéristiques techniques', carac, 54 * PT, 'ck'));
    }

    /* pro-score : badge, niveau, phrase de cadrage, lien vers la methode */
    if (lettre || res.badge) {
      var bloc = [titreSection('Évaluation Pro-Score Welkit')];

      var visuel = res.badge
        ? { width: 40 * PT, image: res.badge, fit: [38 * PT, 22 * PT] }
        : { width: 40 * PT, text: lettre ? 'PRO-SCORE ' + lettre : 'PRO-SCORE',
            bold: true, fontSize: 10.5, color: KAKI };

      bloc.push({
        columns: [
          visuel,
          {
            width: '*',
            stack: [
              { text: (lettre ? 'Pro-Score ' + lettre + ' — ' : '')
                    + (libelleProScore(d.psNote) || ''),
                bold: true, fontSize: 10.5, margin: [0, 0, 0, 5] },
              { text: 'Le PRO-SCORE est un indicateur exclusif Welkit. Il situe le degré '
                    + 'd\'utilisation recommandé d\'un produit par rapport aux autres produits '
                    + 'de sa famille. L\'évaluation porte sur tout le cycle de vie du produit, '
                    + 'de sa conception à sa fin de vie.',
                fontSize: 8.8, color: GRIS_TX, lineHeight: 1.4, alignment: 'justify' }
            ],
            margin: [8, 0, 0, 0]
          }
        ],
        columnGap: 0,
        margin: [0, 0, 0, 8]
      });

      bloc.push({
        text: [{ text: 'Méthode complète : ', color: GRIS_TX },
               { text: 'welkit.com/pages/pro-score', color: BLEU,
                 link: 'https://welkit.com/pages/pro-score' }],
        fontSize: 8.6, margin: [0, 0, 0, 16]
      });

      c.push({ stack: bloc, unbreakable: true });
    }

    /* déclinaisons */
    if (vars.length) {
      c.push(sectionTableau(
        'Déclinaisons référencées',
        ['Déclinaison', 'Code article Welkit', 'Code EAN'],
        vars,
        ['*', 118, 118]
      ));
    }

    /* encadré aide */
    c.push({
      table: {
        widths: ['*'],
        body: [[{
          text: [
            { text: 'Besoin d\'aide ? ', bold: true },
            { text: 'Contactez-nous au ' },
            { text: d.tel || '', bold: true },
            { text: ' ou à l\'adresse ' },
            { text: d.email || '', bold: true }
          ],
          fontSize: 9.5, margin: [10, 8, 10, 8]
        }]]
      },
      layout: {
        hLineWidth: function () { return 0.6; }, vLineWidth: function () { return 0.6; },
        hLineColor: function () { return ORANGE; }, vLineColor: function () { return ORANGE; },
        paddingLeft: function () { return 0; }, paddingRight: function () { return 0; },
        paddingTop: function () { return 0; }, paddingBottom: function () { return 0; }
      }
    });

    /* mention verticale, en image */
    var hMarge = PAGE_H - MH - MB - 20;
    var vertical = texteVertical(d.marge || '', hMarge);

    return {
      pageSize: 'A4',
      pageMargins: [MG, MH, MD, MB],
      info: {
        title: 'FT - ' + titreCourt(d.titre),
        author: 'WELKIT SARL',
        subject: (d.titre || '') + (d.ref ? ' — réf. ' + d.ref : ''),
        creator: 'WELKIT SARL'
      },
      defaultStyle: { fontSize: 9.5, color: NOIR, lineHeight: 1.25 },

      /* fond : filigrane W + mention de propriété verticale en marge gauche */
      background: function () {
        var b = [];
        /* Filigrane W retire : il degradait la lisibilite des tableaux. */
        if (vertical) {
          b.push({ image: vertical, width: 12, absolutePosition: { x: 6.5 * PT, y: MH } });
        }
        return b;
      },

      header: function (page, total) {
        return {
          margin: [MG, 20, MD, 0],
          stack: [
            {
              columns: [
                res.logo
                  ? { image: res.logo, width: 40 * PT }
                  : { text: 'WELKIT', bold: true, fontSize: 18, width: 40 * PT },
                {
                  width: '*',
                  stack: [
                    { text: 'FICHE TECHNIQUE PRODUIT', bold: true, fontSize: 12,
                      color: KAKI, alignment: 'right' },
                    { text: 'Édition du ' + edition + '   ·   Page ' + page + '/' + total,
                      fontSize: 8, color: GRIS_TX, alignment: 'right', margin: [0, 5, 0, 0] }
                  ]
                }
              ],
              columnGap: 12
            },
            {
              canvas: [{ type: 'line', x1: 0, y1: 0, x2: LARG, y2: 0,
                         lineWidth: 0.6, lineColor: GRIS_L }],
              margin: [0, 9, 0, 0]
            }
          ]
        };
      },

      footer: function () {
        return {
          margin: [MG, 0, MD, 0],
          text: (d.legal1 || '') + ' · Document généré le ' + edition,
          fontSize: 6.8, lineHeight: 1.4, color: GRIS_TX
        };
      },

      content: c
    };
  }

  function entete() {
    return {
      hLineWidth: function () { return 0; },
      vLineWidth: function () { return 0; },
      fillColor: function (i) {
        if (i === 0) return KAKI;
        return (i % 2 === 0) ? GRIS : null;
      },
      paddingLeft: function (i) { return i === 0 ? 7 : 0; },
      paddingRight: function () { return 7; },
      paddingTop: function () { return 4.5; },
      paddingBottom: function () { return 4.5; }
    };
  }

  var STYLES = {
    h1:  { fontSize: 15,   bold: true, lineHeight: 1.25, margin: [0, 0, 0, 4] },
    sub: { fontSize: 9.5,  color: GRIS_TX, lineHeight: 1.35 },
    sec: { fontSize: 10.5, bold: true, color: '#FFFFFF' },
    body:{ fontSize: 10.5, lineHeight: 1.45, alignment: 'justify' },
    ck:  { fontSize: 9.5,  bold: true, color: KAKI, lineHeight: 1.35 },
    cv:  { fontSize: 9.5,  lineHeight: 1.35 },
    pht: { fontSize: 9.5,  bold: true, color: BLEU, lineHeight: 1.35 },
    th:  { fontSize: 9,    bold: true, color: KAKI },
    td:  { fontSize: 9.2,  lineHeight: 1.32 }
  };

  /* ------------------------------------------------------------ câblage */

  function lireDonnees(btn) {
    var w = btn.closest('[data-wk-ft-wrap]');
    var t = w && w.querySelector('[data-wk-ft-data]');
    if (!t) return null;
    try { return JSON.parse(t.textContent); } catch (e) { return null; }
  }

  function afficherErreur(btn, msg) {
    var w = btn.closest('[data-wk-ft-wrap]');
    if (!w) return;
    var z = w.querySelector('[data-wk-ft-err]');
    if (!z) {
      z = document.createElement('p');
      z.setAttribute('data-wk-ft-err', '');
      z.style.cssText = 'margin:.8rem 0 0;padding:.8rem 1rem;font-size:1.2rem;'
        + 'line-height:1.4;color:#090302;background:#FEA72B;border-radius:.3rem;';
      w.appendChild(z);
    }
    z.textContent = 'Génération impossible : ' + msg;
  }

  function surClic(e) {
    var btn = e.target.closest('[data-wk-ft]');
    if (!btn || btn.disabled) return;

    var label = btn.querySelector('[data-wk-ft-label]');
    var initial = label ? label.textContent : '';
    var d = lireDonnees(btn);

    var w = btn.closest('[data-wk-ft-wrap]');
    var old = w && w.querySelector('[data-wk-ft-err]');
    if (old) old.remove();

    if (!d) { afficherErreur(btn, 'données produit illisibles'); return; }

    btn.disabled = true;
    if (label) label.textContent = 'Génération du PDF…';

    Promise.all([
      chargerLib(),
      chargerImages(liste(d.imagesRaw)),
      imageDataURL(d.psImage),
      imageDataURL(d.logo),
      imageDataURL(d.filigrane)
    ]).then(function (r) {
      var res = { images: r[1], badge: r[2], logo: r[3], filigrane: r[4] };
      var doc = construire(d, res);
      doc.styles = STYLES;
      r[0].createPdf(doc).download(nomFichier(d));
      btn.disabled = false;
      if (label) label.textContent = initial;
    }).catch(function (err) {
      console.error('[wk-fiche-technique]', err);
      btn.disabled = false;
      if (label) label.textContent = initial;
      afficherErreur(btn, (err && err.message) ? err.message : String(err));
    });
  }

  document.addEventListener('click', surClic);
})();